/** The prior art library's own reads off the task and the analysis: which files it holds, how many, and what the disclosure says of each. */

import type { AnalysisResponse, FileResponse } from "@patent-studio/api-types";

/** The prior art on a task, in the order the task lists it. */
export function priorArtOf(
  files: readonly FileResponse[] | undefined,
): FileResponse[] {
  return (files ?? []).filter((f) => f.file_type === "prior_art");
}

/** The count above the list, in the words the design uses. */
export function libraryCountLabel(count: number): string {
  return `${count} prior art uploaded`;
}

/** How many rows the list shows before the attorney asks for the rest. */
export const VISIBLE_BY_DEFAULT = 5;

/** The rows on screen: everything once expanded, the first five otherwise. */
export function visibleRows(
  files: readonly FileResponse[],
  expanded: boolean,
): FileResponse[] {
  return expanded ? [...files] : files.slice(0, VISIBLE_BY_DEFAULT);
}

/** How many rows show more would reveal. */
export function hiddenCount(
  files: readonly FileResponse[],
  expanded: boolean,
): number {
  return expanded ? 0 : Math.max(0, files.length - VISIBLE_BY_DEFAULT);
}

/**
 * The selection reduced to files that still exist, so a row deleted from
 * under a tick cannot keep the bulk bar counting it.
 */
export function liveSelection(
  selected: ReadonlySet<string>,
  files: readonly FileResponse[],
): Set<string> {
  const ids = new Set(files.map((f) => f.file_id));
  return new Set([...selected].filter((id) => ids.has(id)));
}

/**
 * Each read document's points of disagreement between the disclosure's
 * account of it and its own text, by file id, off the served analysis. A
 * document the analysis has not read has none.
 */
export function accountsOf(
  analysis: AnalysisResponse | undefined,
): Readonly<Record<string, readonly string[]>> {
  return Object.fromEntries(
    (analysis?.library ?? []).map((entry) => [
      entry.file_id,
      entry.disagreements,
    ]),
  );
}
